import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Compass, HeartHandshake, BadgePercent, ChevronDown, Sparkles } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const HERO_IMG =
    "https://images.unsplash.com/photo-1783599677031-29a65dc9bce0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjByZXNvcnQlMjBhZXJpYWwlMjBwb29sfGVufDB8fHx8MTc5MDI4MjYzNXww&ixlib=rb-4.1.0&q=85";

const HEADLINE = [
    <>O refúgio perfeito</>,
    <>para as férias da sua</>,
    <>
        <span className="italic text-gold">família</span> começa aqui
    </>,
];

const TRUST = [
    { icon: Compass, label: "Curadoria independente", sub: "Só resorts que valem a viagem" },
    { icon: BadgePercent, label: "Condições diferenciadas", sub: "Para quem solicita cotação pelo portal" },
    { icon: HeartHandshake, label: "Atendimento humano", sub: "Especialistas de verdade" },
];

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section id="inicio" ref={ref} className="relative flex min-h-[100svh] flex-col overflow-hidden bg-forest-deep" data-testid="hero-section">
            <motion.div style={{ y }} className="absolute inset-0">
                <motion.img
                    src={HERO_IMG}
                    alt="Vista aérea de resort com piscinas e natureza"
                    className="h-full w-full object-cover"
                    initial={{ scale: 1.15 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 2.6, ease: EASE }}
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/35" />

            <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-4 pb-16 pt-24 sm:px-6">
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                    className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-gold/40 bg-white/10 px-4 py-2 backdrop-blur-md"
                    data-testid="hero-badge"
                >
                    <Sparkles className="h-3.5 w-3.5 text-gold" />
                    <span className="text-xs font-medium tracking-wide text-white/90">
                        Curadoria de resorts · valores diferenciados para quem cota aqui
                    </span>
                </motion.div>

                <h1 className="max-w-4xl font-serif text-4xl font-semibold leading-[1.12] tracking-tight text-white sm:text-6xl sm:leading-[1.06] lg:text-7xl" data-testid="hero-headline">
                    {HEADLINE.map((line, i) => (
                        <span key={i} className="block overflow-hidden pb-1">
                            <motion.span
                                className="block"
                                initial={{ y: "110%" }}
                                animate={{ y: 0 }}
                                transition={{ duration: 1, delay: 0.25 + i * 0.18, ease: EASE }}
                            >
                                {line}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
                    className="mt-6 max-w-2xl text-base leading-relaxed text-stone-200 sm:text-lg"
                    data-testid="hero-subtitle"
                >
                    Compare os melhores resorts do Brasil e solicite sua cotação com valores diferenciados —
                    condições exclusivas para quem reserva pelo portal, direto no seu WhatsApp.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1, ease: EASE }}
                    className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
                >
                    <button
                        type="button"
                        data-testid="hero-cta-quote"
                        onClick={() => scrollToId("cotacao-rapida")}
                        className="group inline-flex items-center justify-center gap-2 rounded-full bg-sun px-8 py-4 text-sm font-semibold text-forest-deep shadow-xl shadow-amber-900/30 transition-all duration-200 hover:-translate-y-px hover:bg-gold hover:text-white active:scale-[0.98]"
                    >
                        Solicite sua cotação
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                    <button
                        type="button"
                        data-testid="hero-cta-catalog"
                        onClick={() => scrollToId("explore-resorts")}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 bg-white/10 px-8 py-4 text-sm font-semibold text-white backdrop-blur-md transition-all duration-200 hover:border-gold hover:bg-white/20 active:scale-[0.98]"
                    >
                        Explore os resorts
                    </button>
                </motion.div>

                <div className="mt-14 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {TRUST.map((t, i) => (
                        <motion.div
                            key={t.label}
                            initial={{ opacity: 0, y: 26 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.2 + i * 0.15, ease: EASE }}
                            className="flex items-center gap-4 rounded-2xl border border-white/15 bg-white/10 px-5 py-4 backdrop-blur-xl"
                            data-testid={`hero-trust-${i}`}
                        >
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold/20 text-gold">
                                <t.icon className="h-5 w-5" />
                            </span>
                            <span>
                                <span className="block text-sm font-semibold text-white">{t.label}</span>
                                <span className="block text-xs text-stone-300">{t.sub}</span>
                            </span>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            <motion.button
                type="button"
                aria-label="Rolar para o catálogo"
                onClick={() => scrollToId("explore-resorts")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.9, duration: 0.8 }}
                className="absolute bottom-5 left-1/2 z-10 -translate-x-1/2 text-white/70 transition-colors hover:text-gold"
                data-testid="hero-scroll-indicator"
            >
                <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} className="block">
                    <ChevronDown className="h-6 w-6" />
                </motion.span>
            </motion.button>
        </section>
    );
}
